import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  API_TOKEN_SCOPES,
  API_TOKEN_SCOPE_LABELS,
  type ApiTokenScope,
  type CreatedApiToken,
} from '@nutrigeist/shared';
import { tokenApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { TrashIcon } from './icons.js';

/**
 * API tokens for automations.
 *
 * The plaintext is shown once, right after creation, and never again — the
 * server only keeps its hash. The UI has to make that unmistakable, because a
 * token quietly missed here is a token the user has to create all over again.
 */
export function ApiTokens(): JSX.Element {
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiTokenScope[]>(['activity:write']);
  const [created, setCreated] = useState<CreatedApiToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tokens = useQuery({ queryKey: ['tokens'], queryFn: () => tokenApi.list() });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['tokens'] }).then(() => undefined);

  const createToken = useMutation({
    mutationFn: () => tokenApi.create({ name: name.trim(), scopes }),
    onSuccess: async (result) => {
      setCreated(result);
      setCopied(false);
      setName('');
      setError(null);
      await invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Token konnte nicht erstellt werden.'),
  });

  const revokeToken = useMutation({
    mutationFn: (id: string) => tokenApi.revoke(id),
    onSuccess: invalidate,
  });

  function toggleScope(scope: ApiTokenScope): void {
    setScopes((current) =>
      current.includes(scope) ? current.filter((value) => value !== scope) : [...current, scope],
    );
  }

  return (
    <div className="card stack">
      <div className="card-title">API-Tokens</div>
      <span className="hint">
        Für Automatisierungen wie Tasker. Ein Token gilt dauerhaft und lässt sich nur auf das
        beschränken, was es wirklich braucht — vergib so wenige Rechte wie möglich.
      </span>

      {created && (
        <div className="banner banner-warning stack" role="status">
          <strong>Token „{created.token.name}" erstellt</strong>
          <span>
            Kopiere ihn jetzt. Er wird <strong>nur dieses eine Mal</strong> angezeigt — gespeichert
            ist nur seine Prüfsumme.
          </span>
          <code
            style={{
              display: 'block',
              wordBreak: 'break-all',
              background: 'var(--surface-raised)',
              padding: '8px 10px',
              borderRadius: 8,
              fontSize: '0.85rem',
            }}
          >
            {created.plaintext}
          </code>
          <div className="row">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                void navigator.clipboard?.writeText(created.plaintext).then(() => setCopied(true));
              }}
            >
              {copied ? 'Kopiert' : 'Kopieren'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setCreated(null)}>
              Fertig
            </button>
          </div>
        </div>
      )}

      <div className="field">
        <label htmlFor="token-name">Name</label>
        <input
          id="token-name"
          className="input"
          placeholder="z. B. Tasker (Handy)"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className="field">
        <label>Berechtigungen</label>
        <div className="stack" style={{ gap: 6 }}>
          {API_TOKEN_SCOPES.map((scope) => (
            <label className="row" key={scope} style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={() => toggleScope(scope)}
              />
              <span>
                {API_TOKEN_SCOPE_LABELS[scope]} <span className="hint">({scope})</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {error && (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}

      <button
        className="btn btn-primary btn-block"
        disabled={createToken.isPending || name.trim().length === 0 || scopes.length === 0}
        onClick={() => createToken.mutate()}
      >
        {createToken.isPending ? <span className="spinner" /> : 'Token erstellen'}
      </button>

      {tokens.data && tokens.data.tokens.length > 0 && (
        <div className="entry-list">
          {tokens.data.tokens.map((token) => (
            <div className="entry" key={token.id}>
              <div className="grow">
                <div className="entry-name">{token.name}</div>
                <div className="entry-meta">
                  {token.preview}… · {token.scopes.join(', ')}
                  {' · '}
                  {token.lastUsedAt
                    ? `zuletzt genutzt ${new Date(token.lastUsedAt).toLocaleDateString('de-DE')}`
                    : 'noch nie genutzt'}
                </div>
              </div>
              <button
                className="icon-btn"
                aria-label={`${token.name} widerrufen`}
                disabled={revokeToken.isPending}
                onClick={() => revokeToken.mutate(token.id)}
              >
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
